import { trpc } from '@/lib/trpc';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { GuardrailForm, GuardrailFormData } from './add';
import { Spinner } from '@televet/kibble-ui/Spinner';
import { Flex } from '@televet/kibble-ui/chakra';
import { toaster } from '@/components/ui/toaster';

export const Route = createFileRoute(
  '/_authenticated/guardrails/$guardrailId'
)({
  component: EditGuardrail,
  beforeLoad: () => {
    return {
      pageTitle: 'Edit Guardrail',
    };
  },
});

function EditGuardrail() {
  const { guardrailId } = Route.useParams();
  const navigate = useNavigate();

  const { data: guardrail, isLoading } = useQuery(
    trpc.guardrail.findOne.queryOptions({ id: guardrailId })
  );

  const { mutate } = useMutation(
    trpc.guardrail.updateOne.mutationOptions({
      onSuccess: (data) => {
        toaster.success({
          title: 'Guardrail updated successfully',
          description: data.message,
        });
        navigate({ to: '/guardrails' });
      },
      onError: (error) => {
        const errorMessage = error.message || 'Failed to update guardrail';
        toaster.error({
          title: 'Failed to update guardrail',
          description: errorMessage,
          duration: 10000,
          closable: true,
        });
      },
    })
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<GuardrailFormData>({
    values: guardrail
      ? {
          name: guardrail.name,
          guardrailIdentifier: guardrail.guardrailIdentifier,
          guardrailVersion: guardrail.guardrailVersion,
        }
      : undefined,
  });

  const onSubmit = (data: GuardrailFormData) => {
    mutate({
      id: guardrailId,
      data: {
        name: data.name,
        guardrailIdentifier: data.guardrailIdentifier,
        guardrailVersion: data.guardrailVersion,
      },
    });
  };

  const onCancel = () => {
    navigate({ to: '/guardrails' });
  };

  if (isLoading) {
    return (
      <Flex justify="center" align="center" h="100%">
        <Spinner />
      </Flex>
    );
  }

  return (
    <GuardrailForm
      onSubmit={onSubmit}
      onCancel={onCancel}
      errors={errors}
      register={register}
      handleSubmit={handleSubmit}
    />
  );
}
